import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { LoginUser } from "../redux/actions/auth";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  PersonOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
  SportsSoccer,
} from "@mui/icons-material";
import info from "../../package.json";
import { WebauthnFlow, WEBAUTHN_ENABLED } from "../features/webauthn";

const Login = () => {
  let history = useHistory();
  let _location = useLocation();
  let { from } = _location.state || { from: { pathname: "/" } };

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { isAuthenticated, login_error, loading, webauthn } = useSelector((state) => ({
    isAuthenticated: state.auth.isAuthenticated,
    login_error: state.auth.login_error,
    loading: state.auth.loading,
    webauthn: state.auth.webauthn,
  }));

  const dispatch = useDispatch();

  const _handleLogin = () => {
    if (!username || !password) return;
    dispatch(LoginUser(username, password));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") _handleLogin();
  };

  useEffect(() => {
    if (login_error) {
      setPassword("");
    }
  }, [login_error]);

  useEffect(() => {
    if (isAuthenticated) history.replace(from);
  }, [history, from, isAuthenticated]);

  // WebAuthn second factor: once the password step returns a pre-auth state,
  // hand the screen over to the ceremony flow until a session is created.
  if (WEBAUTHN_ENABLED && webauthn) {
    return <WebauthnFlow />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: "#0f1115",
      }}
    >
      {/* Left Branding Panel */}
      <Stack
        sx={{
          flex: { xs: "none", md: 1 },
          height: { xs: "220px", md: "100vh" },
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #1a1a2e 0%, #0f1115 50%, #1a0f00 100%)",
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            border: "1px solid rgba(232, 108, 0, 0.08)",
            top: "10%",
            left: "-10%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            border: "1px solid rgba(232, 108, 0, 0.05)",
            bottom: "15%",
            right: "-5%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            backgroundColor: "rgba(232, 108, 0, 0.03)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />

        <Stack alignItems="center" gap={2} sx={{ position: "relative", zIndex: 1 }}>
          <Stack
            sx={{
              width: { xs: 48, md: 64 },
              height: { xs: 48, md: 64 },
              borderRadius: "16px",
              backgroundColor: "rgba(232, 108, 0, 1)",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 32px rgba(232, 108, 0, 0.3)",
            }}
          >
            <img
              src="/stepout-icon.svg"
              alt="StepOut"
              style={{ width: "70%", height: "70%", objectFit: "contain" }}
            />
          </Stack>

          <Stack alignItems="center" gap={0.5}>
            <Typography
              sx={{
                fontSize: { xs: "28px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-1px",
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              Analyst Portal
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "13px", md: "15px" },
                color: "rgba(255,255,255,0.45)",
                textAlign: "center",
                fontWeight: 400,
                letterSpacing: "0.3px",
              }}
            >
              Integrated analysis, review & ticketing
            </Typography>
          </Stack>

          {/* Stats decoration - desktop only */}
          <Stack
            direction="row"
            gap={3}
            sx={{
              mt: 3,
              display: { xs: "none", md: "flex" },
            }}
          >
            {[
              { val: "35000+", label: "Matches Analysed" },
              { val: "7500+", label: "Teams Analysed" },
              { val: "170000+", label: "Players Analysed" },
            ].map((stat) => (
              <Stack key={stat.label} alignItems="center" gap={0.25}>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "rgba(232, 108, 0, 1)",
                  }}
                >
                  {stat.val}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.3)",
                    fontWeight: 500,
                  }}
                >
                  {stat.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>

      {/* Right Login Panel */}
      <Stack
        sx={{
          flex: { xs: 1, md: 1 },
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--app-bg)",
          px: 3,
          py: { xs: 4, md: 0 },
          position: "relative",
        }}
      >
        <Stack
          sx={{
            width: "100%",
            maxWidth: "380px",
            gap: 3,
          }}
        >
          {/* Welcome text */}
          <Stack gap={0.5}>
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                color: "var(--text-1)",
                letterSpacing: "-0.5px",
              }}
            >
              Welcome back
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "var(--text-2)",
              }}
            >
              Sign in to your analyst account to continue
            </Typography>
          </Stack>

          {/* Error */}
          {login_error && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                fontSize: "13px",
                "& .MuiAlert-icon": { fontSize: "18px" },
              }}
            >
              Invalid credentials. Please try again.
            </Alert>
          )}

          {/* Form */}
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--text-2)",
                }}
              >
                Username
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutline
                        sx={{ fontSize: 18, color: "var(--text-2)" }}
                      />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "10px",
                    fontSize: "14px",
                    height: 44,
                    backgroundColor: "var(--surface-1)",
                    "& fieldset": {
                      borderColor: "var(--border-1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--text-2) !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(232, 108, 0, 1) !important",
                      borderWidth: "1.5px !important",
                    },
                  },
                }}
              />
            </Stack>

            <Stack gap={0.5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--text-2)",
                }}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined
                        sx={{ fontSize: 18, color: "var(--text-2)" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ color: "var(--text-2)" }}
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: 18 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "10px",
                    fontSize: "14px",
                    height: 44,
                    backgroundColor: "var(--surface-1)",
                    "& fieldset": {
                      borderColor: "var(--border-1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--text-2) !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(232, 108, 0, 1) !important",
                      borderWidth: "1.5px !important",
                    },
                  },
                }}
              />
            </Stack>
          </Stack>

          {/* Login Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={_handleLogin}
            disabled={loading || !username || !password}
            sx={{
              height: 46,
              borderRadius: "10px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 700,
              letterSpacing: "0.3px",
              backgroundColor: "rgba(232, 108, 0, 1)",
              boxShadow: "0 4px 16px rgba(232, 108, 0, 0.3)",
              "&:hover": {
                backgroundColor: "rgba(200, 93, 0, 1)",
                boxShadow: "0 6px 20px rgba(232, 108, 0, 0.4)",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(232, 108, 0, 0.4)",
                color: "rgba(255,255,255,0.6)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Sign In"
            )}
          </Button>

          {/* Footer info */}
          <Stack alignItems="center" gap={1} sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <SportsSoccer
                sx={{ fontSize: 14, color: "var(--text-2)", opacity: 0.5 }}
              />
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "var(--text-2)",
                  opacity: 0.6,
                }}
              >
                StepOut Play · Analyst Portal v{info.version}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Login;
